import * as tf from "@tensorflow/tfjs";
import * as faceDetection from "@tensorflow-models/face-detection";

const DEFAULT_CONFIG = {
	maxFaces: 2,
	modelType: "short",
	skipFrames: 10,
	roi: { x: 0.15, y: 0.1, w: 0.7, h: 0.8 },
	thresholds: {
		edgeMargin: 0.02,
		minAreaRatio: 0.01,
		maxAreaRatio: 0.75,
		centerTolerance: 0.35,
		lookAwayRatio: 0.4,
	},
};

export const FaceDetectionFlag = {
	NO_FACE: "NO_FACE",
	MULTIPLE_FACES: "MULTIPLE_FACES",
	FACE_CLIPPED: "FACE_CLIPPED",
	TOO_FAR: "TOO_FAR",
	TOO_CLOSE: "TOO_CLOSE",
	OFF_CENTER: "OFF_CENTER",
	OUTSIDE_ROI: "OUTSIDE_ROI",
	LOOKING_AWAY: "LOOKING_AWAY",
	OK: "OK",
};

export const FLAG_META = {
	[FaceDetectionFlag.NO_FACE]: {
		message: "No face detected",
		severity: "error",
	},
	[FaceDetectionFlag.MULTIPLE_FACES]: {
		message: "Multiple faces detected",
		severity: "error",
	},
	[FaceDetectionFlag.OUTSIDE_ROI]: {
		message: "Face outside detection area",
		severity: "warn",
	},
	[FaceDetectionFlag.FACE_CLIPPED]: {
		message: "Face partially out of frame",
		severity: "warn",
	},
	[FaceDetectionFlag.TOO_FAR]: {
		message: "Move closer to camera",
		severity: "warn",
	},
	[FaceDetectionFlag.TOO_CLOSE]: {
		message: "Move back from camera",
		severity: "warn",
	},
	[FaceDetectionFlag.OFF_CENTER]: {
		message: "Center your face",
		severity: "warn",
	},
	[FaceDetectionFlag.LOOKING_AWAY]: {
		message: "Please face the camera",
		severity: "warn",
	},
	[FaceDetectionFlag.OK]: {
		message: "Face detected",
		severity: "ok",
	},
};

const FLAG_PRIORITY = [
	FaceDetectionFlag.NO_FACE,
	FaceDetectionFlag.MULTIPLE_FACES,
	FaceDetectionFlag.OUTSIDE_ROI,
	FaceDetectionFlag.FACE_CLIPPED,
	FaceDetectionFlag.TOO_FAR,
	FaceDetectionFlag.TOO_CLOSE,
	FaceDetectionFlag.OFF_CENTER,
	FaceDetectionFlag.LOOKING_AWAY,
	FaceDetectionFlag.OK,
];

class FaceDetectionMonitor {
	constructor(config = {}) {
		this.config = {
			...DEFAULT_CONFIG,
			...config,
			roi: { ...DEFAULT_CONFIG.roi, ...(config.roi || {}) },
			thresholds: {
				...DEFAULT_CONFIG.thresholds,
				...(config.thresholds || {}),
			},
		};

		this.detector = null;
		this.running = false;
		this.frameIndex = 0;
		this.animationFrameId = null;
		this.lastFaces = [];
		this.lastInferenceMs = 0;
		this.fpsFrames = 0;
		this.fpsLastTime = performance.now();
		this.fpsValue = 0;
		this.callbacks = {};
		this.videoElement = null;
		this.overlayCanvas = null;
		this.ctx = null;
	}

	async init() {
		if (this.detector) return;
		await tf.ready();
		this.detector = await faceDetection.createDetector(
			faceDetection.SupportedModels.MediaPipeFaceDetector,
			{
				runtime: "tfjs",
				maxFaces: this.config.maxFaces,
				modelType: this.config.modelType,
			}
		);
	}

	async start({ videoElement, overlayCanvas = null, onUpdate, onError }) {
		if (!videoElement) return;
		this.stop();
		this.videoElement = videoElement;
		this.overlayCanvas = overlayCanvas;
		this.ctx = this.overlayCanvas ? this.overlayCanvas.getContext("2d") : null;
		this.callbacks = { onUpdate, onError };

		try {
			await this.init();
			this.running = true;
			this.loop();
		} catch (error) {
			this.running = false;
			if (this.callbacks.onError) {
				this.callbacks.onError(error);
			}
		}
	}

	stop() {
		this.running = false;
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
		if (this.ctx && this.overlayCanvas) {
			this.ctx.clearRect(
				0,
				0,
				this.overlayCanvas.width,
				this.overlayCanvas.height
			);
		}
	}

	destroy() {
		this.stop();
		if (this.detector && this.detector.dispose) {
			this.detector.dispose();
		}
		this.detector = null;
	}

	loop = async () => {
		if (!this.running) return;
		this.frameIndex += 1;
		this.updateFps();
		this.syncCanvasDimensions();

		if (this.frameIndex % this.config.skipFrames === 0) {
			await this.detectAndDraw();
		} else {
			this.draw(this.lastFaces);
		}

		this.animationFrameId = requestAnimationFrame(this.loop);
	};

	syncCanvasDimensions() {
		if (!this.videoElement) return;
		const { width, height } = this.getFrameSize();
		if (!width || !height) return;
		this.syncVideoElementDimensions(width, height);
		if (!this.overlayCanvas) return;
		if (this.overlayCanvas.width !== width) {
			this.overlayCanvas.width = width;
		}
		if (this.overlayCanvas.height !== height) {
			this.overlayCanvas.height = height;
		}
	}

	getFrameSize() {
		if (!this.videoElement) {
			return { width: 0, height: 0 };
		}

		return {
			width: Math.round(
				this.videoElement.videoWidth ||
					this.videoElement.clientWidth ||
					this.videoElement.width ||
					0
			),
			height: Math.round(
				this.videoElement.videoHeight ||
					this.videoElement.clientHeight ||
					this.videoElement.height ||
					0
			),
		};
	}

	syncVideoElementDimensions(width, height) {
		if (!this.videoElement || !width || !height) return;
		if (this.videoElement.width !== width) {
			this.videoElement.width = width;
		}
		if (this.videoElement.height !== height) {
			this.videoElement.height = height;
		}
	}

	async detectAndDraw() {
		if (
			!this.detector ||
			!this.videoElement ||
			this.videoElement.readyState < 2
		) {
			return;
		}

		const { width, height } = this.getFrameSize();
		if (!width || !height) return;
		this.syncVideoElementDimensions(width, height);

		const t0 = performance.now();
		try {
			this.lastFaces = await this.detector.estimateFaces(this.videoElement, {
				flipHorizontal: false,
			});
		} catch (error) {
			this.lastFaces = [];
			if (this.callbacks.onError) {
				this.callbacks.onError(error);
			}
		}
		this.lastInferenceMs = performance.now() - t0;

		const flags = this.analyze(this.lastFaces);
		this.draw(this.lastFaces);
		const primaryFlag = FLAG_PRIORITY.find((flag) => flags.includes(flag));

		if (this.callbacks.onUpdate) {
			this.callbacks.onUpdate({
				faces: this.lastFaces,
				flags,
				primaryFlag,
				primaryMeta: primaryFlag ? FLAG_META[primaryFlag] : null,
				inferenceMs: this.lastInferenceMs,
				fps: this.fpsValue,
			});
		}
	}

	draw(faces) {
		if (!this.ctx || !this.overlayCanvas) return;
		const w = this.overlayCanvas.width;
		const h = this.overlayCanvas.height;
		this.ctx.clearRect(0, 0, w, h);
		this.drawROIBorder(w, h);

		for (const face of faces) {
			const inside = this.isFaceCenterInsideROI(face, w, h);
			this.drawFaceBox(face, inside);
		}
	}

	drawROIBorder(w, h) {
		const r = this.roiToPixels(w, h);
		this.ctx.strokeStyle = "#22d3a7";
		this.ctx.lineWidth = 2;
		this.ctx.setLineDash([8, 4]);
		this.ctx.strokeRect(r.x, r.y, r.w, r.h);
		this.ctx.setLineDash([]);
	}

	drawFaceBox(face, insideROI) {
		const box = face.box;
		this.ctx.strokeStyle = insideROI ? "#22c55e" : "#ef4444";
		this.ctx.lineWidth = 2;
		this.ctx.strokeRect(box.xMin, box.yMin, box.width, box.height);
	}

	analyze(faces) {
		const flags = [];
		const { width: w, height: h } = this.getFrameSize();
		if (!w || !h) {
			flags.push(FaceDetectionFlag.NO_FACE);
			return flags;
		}
		const frameArea = w * h;
		const thresholds = this.config.thresholds;
		const roiPx = this.roiToPixels(w, h);

		if (!faces.length) {
			flags.push(FaceDetectionFlag.NO_FACE);
			return flags;
		}

		if (faces.length > 1) {
			flags.push(FaceDetectionFlag.MULTIPLE_FACES);
		}

		const primary = faces.reduce((a, b) =>
			a.box.width * a.box.height > b.box.width * b.box.height ? a : b
		);

		const box = primary.box;
		const faceArea = box.width * box.height;
		const areaRatio = faceArea / frameArea;
		const insideROI = this.isFaceCenterInsideROI(primary, w, h);
		if (!insideROI) {
			flags.push(FaceDetectionFlag.OUTSIDE_ROI);
		}

		const minEdgeDistance = Math.min(
			box.xMin / w,
			box.yMin / h,
			(w - box.xMax) / w,
			(h - box.yMax) / h
		);
		if (minEdgeDistance < thresholds.edgeMargin) {
			flags.push(FaceDetectionFlag.FACE_CLIPPED);
		}
		if (areaRatio < thresholds.minAreaRatio) {
			flags.push(FaceDetectionFlag.TOO_FAR);
		}
		if (areaRatio > thresholds.maxAreaRatio) {
			flags.push(FaceDetectionFlag.TOO_CLOSE);
		}

		const faceCenterX = box.xMin + box.width / 2;
		const faceCenterY = box.yMin + box.height / 2;
		const roiCenterX = roiPx.x + roiPx.w / 2;
		const roiCenterY = roiPx.y + roiPx.h / 2;
		const dx = Math.abs(faceCenterX - roiCenterX) / roiPx.w;
		const dy = Math.abs(faceCenterY - roiCenterY) / roiPx.h;
		if (dx > thresholds.centerTolerance || dy > thresholds.centerTolerance) {
			flags.push(FaceDetectionFlag.OFF_CENTER);
		}

		if (primary.keypoints && primary.keypoints.length >= 6) {
			const keypointsByName = {};
			for (const keypoint of primary.keypoints) {
				keypointsByName[keypoint.name] = keypoint;
			}
			const nose = keypointsByName.noseTip;
			const leftEar = keypointsByName.leftEarTragion;
			const rightEar = keypointsByName.rightEarTragion;

			if (nose && leftEar && rightEar) {
				const leftDist = Math.hypot(nose.x - leftEar.x, nose.y - leftEar.y);
				const rightDist = Math.hypot(nose.x - rightEar.x, nose.y - rightEar.y);
				const earRatio =
					Math.min(leftDist, rightDist) / (Math.max(leftDist, rightDist) || 1);
				if (earRatio < thresholds.lookAwayRatio) {
					flags.push(FaceDetectionFlag.LOOKING_AWAY);
				}
			}
		}

		if (!flags.length) {
			flags.push(FaceDetectionFlag.OK);
		}

		return flags;
	}

	roiToPixels(width, height) {
		const roi = this.config.roi;
		return {
			x: roi.x * width,
			y: roi.y * height,
			w: roi.w * width,
			h: roi.h * height,
		};
	}

	isFaceCenterInsideROI(face, width, height) {
		const box = face.box;
		const centerX = box.xMin + box.width / 2;
		const centerY = box.yMin + box.height / 2;
		const roi = this.roiToPixels(width, height);
		return (
			centerX >= roi.x &&
			centerX <= roi.x + roi.w &&
			centerY >= roi.y &&
			centerY <= roi.y + roi.h
		);
	}

	updateFps() {
		this.fpsFrames += 1;
		const now = performance.now();
		if (now - this.fpsLastTime >= 1000) {
			this.fpsValue = this.fpsFrames;
			this.fpsFrames = 0;
			this.fpsLastTime = now;
		}
	}
}

export default FaceDetectionMonitor;
